// src/dashboard/pages/teacher/Objectifs/components/ObjectifsMobileCards.jsx

import React from "react";
import { FaCheck, FaEdit, FaTrash } from "react-icons/fa";

import {
  clampProgress,
  formatDateFr,
  getObjectifAdherent,
  getObjectifAdherentName,
  getObjectifGroups,
  OBJECTIF_TYPE_LABELS,
} from "../utils/objectifsDashboardUtils";

function Pill({ children, tone = "slate" }) {
  const tones = {
    slate:
      "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
    emerald:
      "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
    amber:
      "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
    indigo:
      "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20",
  };

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${
        tones[tone] || tones.slate
      }`}
    >
      {children}
    </span>
  );
}

function ProgressBar({ value }) {
  const progress = clampProgress(value);

  return (
    <div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
        <div
          className="h-2 rounded-full bg-gradient-to-r from-emerald-500 via-indigo-500 to-[#4CA82F] transition-all"
          style={{ width: `${progress}%` }}
        />
      </div>

      <p className="mt-1 text-xs font-bold text-slate-600 dark:text-slate-300">
        {progress}%
      </p>
    </div>
  );
}

export default function ObjectifsMobileCards({
  rows = [],
  onEdit,
  onDelete,
  onToggleDone,
  onChangeProgress,
  deletingId,
  updatingProgressId,
}) {
  return (
    <div className="space-y-3 md:hidden">
      {rows.map((row) => {
        const adherent = getObjectifAdherent(row);
        const groups = getObjectifGroups(row);
        const progress = clampProgress(row.progress);

        return (
          <article
            key={`mobile-${row.id}`}
            className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/60"
          >
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
                    {getObjectifAdherentName(row)}
                  </p>

                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    #{row.adherent_id || adherent?.id || "—"} ·{" "}
                    {row.saison || "Saison non renseignée"}
                  </p>
                </div>

                {row.is_done ? (
                  <Pill tone="emerald">Terminé</Pill>
                ) : (
                  <Pill tone="amber">En cours</Pill>
                )}
              </div>

              {groups.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {groups.map((group) => (
                    <Pill key={group.id || group.nom}>{group.nom}</Pill>
                  ))}
                </div>
              ) : null}

              <div className="mt-4">
                <p className="text-sm font-bold text-slate-900 dark:text-white">
                  {row.title || "—"}
                </p>

                {row.description ? (
                  <p className="mt-1 line-clamp-3 text-xs leading-5 text-slate-600 dark:text-slate-300">
                    {row.description}
                  </p>
                ) : null}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {row.type ? (
                  <Pill tone="indigo">
                    {OBJECTIF_TYPE_LABELS[row.type] || row.type}
                  </Pill>
                ) : null}

                {row.due_date ? (
                  <Pill>Échéance : {formatDateFr(row.due_date)}</Pill>
                ) : null}
              </div>

              <div className="mt-4">
                <ProgressBar value={progress} />

                <input
                  type="range"
                  min="0"
                  max="100"
                  value={progress}
                  disabled={updatingProgressId === row.id}
                  onChange={(event) =>
                    onChangeProgress(row, Number(event.target.value))
                  }
                  className="mt-2 w-full accent-[#013E8E]"
                />
              </div>

              <div className="mt-4 flex items-center justify-end gap-2 border-t border-slate-100 pt-3 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => onToggleDone(row)}
                  className={`inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold transition ${
                    row.is_done
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  }`}
                >
                  <FaCheck />
                  {row.is_done ? "Rouvrir" : "Terminer"}
                </button>

                <button
                  type="button"
                  onClick={() => onEdit(row)}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                >
                  <FaEdit />
                  Modifier
                </button>

                <button
                  type="button"
                  onClick={() => onDelete(row)}
                  disabled={deletingId === row.id}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 transition hover:bg-rose-100 disabled:opacity-50 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}